<template>
  <div>
    <SkeletonTable :loading="loading" :first-load="firstLoad" :cols="6" :rows="15"/>
    <v-data-table
      :headers="headers"
      :items="computed_thesis"
      :search="search"
      :custom-filter="filterItems"
      :items-per-page="15"
      :footer-props="{
        itemsPerPageOptions: [15, 30, -1],
        itemsPerPageText: $t('table.itemsPerPageText')
      }"
      sort-by="display_date"
      sort-desc
      class="table-cursor"
      :loading="loading"
      :loading-text="$t('table.loading')"
      v-show="!firstLoad"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('search')"
            single-line
            hide-details
          />
          <v-spacer />
          <v-btn
            color="primary"
            dark
            class="mb-2"
            to="/manage/edit/publications/add/thesis"
            ><v-icon class="mr-2">mdi-plus-thick</v-icon
            >{{ $t("button.newItem") }}</v-btn
          >
        </v-toolbar>
      </template>
      <template v-slot:item.minerva="{ item }">
        <div class="d-flex flex-column align-center justify-center text-center">
          <div class="d-flex align-center">
            <v-tooltip top v-if="item.minerva_sync_status">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  v-bind="attrs"
                  v-on="on"
                  :color="minervaStatusColor(item.minerva_sync_status)"
                  :text-color="minervaStatusTextColor(item.minerva_sync_status)"
                >
                  {{ item.minerva_status_label || minervaStatusLabel(item.minerva_sync_status) }}
                </v-chip>
              </template>
              <div class="py-1">
                <span v-if="minervaStatusDescription(item.minerva_sync_status, item)">
                  {{ minervaStatusDescription(item.minerva_sync_status, item) }}
                </span>
                <template v-if="item.minerva_sync_status !== 'wont-sync'">
                  <span v-if="item.minerva_last_checked_at">
                    {{ $t('form.publication.minervaLastCheckedAt') }}:
                    {{ item.minerva_checked_display || formatMinervaLastCheckedAt(item.minerva_last_checked_at) }}
                  </span>
                  <span v-else>
                    {{ $t('form.publication.minerva.noChecks') }}
                  </span>
                </template>
              </div>
            </v-tooltip>
            <v-btn
              v-if="availableMinervaUrl(item)"
              icon
              small
              class="ml-2"
              :href="availableMinervaUrl(item)"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="$t('form.publication.minerva.summary.available')"
            >
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
          <span
            class="caption mt-1"
            v-if="item.minerva_sync_status === 'missing-data' && (item.minerva_summary_text || minervaMetadataSummary(item, { includeMissingDataDetails: false }))"
          >
            {{ item.minerva_summary_text || minervaMetadataSummary(item, { includeMissingDataDetails: false }) }}
          </span>
        </div>
      </template>
      <template
        v-slot:item.features="{ item }"
      >
        <v-tooltip
          v-if="item.hidden == true"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="red"
              v-bind="attrs"
              v-on="on"
            >
              mdi-eye-off
            </v-icon>
          </template>
          {{ $t('table.features.hidden') }}
        </v-tooltip>
        <v-icon
          v-else
          color="grey"
          v-bind="attrs"
          v-on="on"
        >
          mdi-eye-outline
        </v-icon>
        <v-tooltip
          v-if="item.is_external === true"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="pink"
              v-bind="attrs"
              v-on="on"
            >
              mdi-alien
            </v-icon>
          </template>
          {{ $t('table.features.isExternal') }}
        </v-tooltip>
        <v-icon
          v-else
          color="grey"
          v-bind="attrs"
          v-on="on"
        >
          mdi-alien-outline
        </v-icon>
        <v-tooltip
          v-if="item.featured === true"
          top
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color="purple"
              v-bind="attrs"
              v-on="on"
            >
              mdi-alert-octagram
            </v-icon>
          </template>
          {{ $t('table.features.isFeatured') }}
        </v-tooltip>
        <v-icon
          v-else
          color="grey"
          v-bind="attrs"
          v-on="on"
        >
          mdi-alert-octagram-outline
        </v-icon>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          text
          class="mr-2"
          color="primary"
          :to="`/manage/edit/publications/edit/${item.id}`"
        >
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-icon color="red" @click="deleteItem(item)">
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import axios from "axios";
import SkeletonTable from "../../../components/reusables/SkeletonTable";
import minervaDisplay from "@/mixins/minervaDisplay";
export default {
  mixins: [minervaDisplay],
  components: { SkeletonTable },
  data() {
    return {
      search: "",
      loading: true,
      firstLoad: true,
      thesis: []
    };
  },

  computed: {
    headers() {
      return [
        {
          text: this.$t("title.publication"),
          value: "title",
        },
        {
          text: this.$t("form.authors"),
          value: "authors_text",
        },
        {
          text: this.$t("table.date"),
          value: "display_date"
        },
        {
          text: this.$t("form.features"),
          value: "features",
          filterable: false,
          width: "15%",
          sortable: false
        },
        {
          text: this.$t("table.minervaSync"),
          value: "minerva",
          sortable: false,
          width: "12%"
        },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          width: "15%",
          filterable: false,
          sortable: false
        }
      ];
    },
    computed_thesis: function(){
      let computed_thesis = this.thesis;
      computed_thesis.forEach((part, i, a) => {
        const label = this.minervaStatusLabel(part.minerva_sync_status);
        const summary = this.minervaMetadataSummary(part, { includeMissingDataDetails: false });
        const checked = part.minerva_last_checked_at ? this.formatMinervaLastCheckedAt(part.minerva_last_checked_at) : "";
        a[i].display_date = part.thesis.lecture_date || part.thesis.deposit_date;
        a[i].minerva_status_label = label;
        a[i].minerva_summary_text = summary;
        a[i].minerva_checked_display = checked;
        a[i].minerva = [label, summary, checked].filter(Boolean).join(" ");
      });
      return computed_thesis;
    }
  },
  created() {
    this.initialize();
  },

  methods: {
    initialize() {
      let path = this.$store.state.endpoint + "/publication/type/thesis";
      axios
        .get(path)
        .then(response => {
          this.thesis = response.data;
          this.loading = false;
          if (this.firstLoad) this.firstLoad = false;
        })
        .catch(error => {
          console.log(error);
        });
    },
    deleteItem(item) {
      if (confirm(this.$t("message.confirm.delete"))) {
        let path = this.$store.state.endpoint + "/publication/" + item.id;
        axios
          .delete(path)
          .then(response => {
            const index = this.thesis.indexOf(item);
            this.thesis.splice(index, 1);
            this.$store.commit("alert", {
              color: "success",
              text: this.$t("message.correct.deleteItem")
            });
          })
          .catch(error => {
            console.log(error);
            this.$store.commit("alert", {
              color: "error",
              text: error.response
            });
          });
      }
    },
    filterItems(value, search, item) {
      const search_array = search
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .split(",")
        .map(x => x.trim());
      const today = new Date().toISOString().split('T')[0];
      const thisyear = today.split('-')[0];
      const customValue = (
        item.title +
        " " +
        item.authors_text +
        " " +
        ((item.doi !== null && item.doi.trim() !== "") ? ("doi:" + item.doi) : "no-doi") + 
        (item.is_external !== false ? " external" : "") +
        (item.hidden ? " hidden" : "") +
        (item.research_areas.length === 0 ? " no-areas" : "") +
        " " + (item.language || "") +
        " " + (item.minerva_sync_option || "") +
        " " + (item.minerva || item.minerva_status_label || "no-minerva")
      )
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return (
        customValue != null &&
        search_array != null &&
        typeof customValue === "string" &&
        search_array.every(function(search){ return customValue.indexOf(search) !== -1 })
      );
    },
  },

};
</script>
